<script setup lang="ts">
import { computed } from 'vue';
import { Line } from 'vue-chartjs';
import { RefreshCw, Info } from 'lucide-vue-next';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const props = defineProps<{
    evolutionData: any[];
    loadingEvolution: boolean;
    isRefreshingChart: boolean;
    evolutionError: string;
    selectedCategory: number | null;
    hiddenExtraCategories: Set<string>;
}>();

const emit = defineEmits(['toggle-category-visibility']);

const chartData = computed(() => {
    const labels = props.evolutionData.map(d => {
        const parts = d.date.split('-');
        return `${parts[1]}/${parts[0]}`;
    });

    const lastPastIndex = props.evolutionData.findLastIndex(d => !d.isFuture);

    const baseTotalPast = props.evolutionData.map(d => d.isFuture ? null : d.total);
    const baseTotalFuture = props.evolutionData.map((d, i) => {
        if (!d.isFuture && i !== lastPastIndex) return null;
        return d.total;
    });

    const extraCatSet = new Set<string>();
    props.evolutionData.forEach(d => {
        if (d.isFuture && d.categories) {
            Object.keys(d.categories).forEach(c => extraCatSet.add(c));
        }
    });
    const sortedExtraCats = Array.from(extraCatSet).sort();

    const colors = [
        'rgba(16, 185, 129, 0.25)',
        'rgba(245, 158, 11, 0.25)',
        'rgba(59, 130, 246, 0.25)',
        'rgba(236, 72, 153, 0.25)',
        'rgba(139, 92, 246, 0.25)',
    ];
    const borderColors = [
        '#10b981', '#f59e0b', '#3b82f6', '#ec4899', '#8b5cf6'
    ];

    const datasets = [];

    // Dataset 0: Patrimoine Réalisé
    datasets.push({
        label: 'Patrimoine Réalisé',
        backgroundColor: 'transparent',
        borderColor: '#4f46e5',
        data: baseTotalPast,
        borderWidth: 3,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.1,
        fill: false,
    });

    // Dataset 1: Patrimoine Projeté
    datasets.push({
        label: 'Patrimoine Projeté',
        backgroundColor: 'transparent',
        borderColor: '#4f46e5',
        data: baseTotalFuture,
        borderWidth: 2,
        borderDash: [5, 5],
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.1,
        fill: false,
    });

    sortedExtraCats.forEach((cat, idx) => {
        const isHidden = props.hiddenExtraCategories.has(cat);
        const datasetIdx = datasets.length;

        const layerData = props.evolutionData.map((d, i) => {
            const isFuturePoint = d.isFuture || i === lastPastIndex;
            if (!isFuturePoint) return null;

            let extraSum = 0;
            sortedExtraCats.slice(0, idx + 1).forEach(c => {
                if (!props.hiddenExtraCategories.has(c)) {
                    extraSum += (d.categories && d.categories[c]) ? d.categories[c] : 0;
                }
            });
            return d.total + extraSum;
        });

        datasets.push({
            label: `+ ${cat}`,
            backgroundColor: isHidden ? 'transparent' : colors[idx % colors.length],
            borderColor: isHidden ? 'transparent' : borderColors[idx % borderColors.length],
            data: layerData,
            borderWidth: isHidden ? 0 : 1.5,
            fill: datasetIdx - 1,
            pointRadius: 0,
            tension: 0.1,
            borderDash: [3, 3],
        });
    });

    return { labels, datasets };
});

const chartOptions = computed(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    scales: {
        y: {
            beginAtZero: true,
            ticks: {
                callback: (v: any) => new Intl.NumberFormat('fr-FR').format(v) + ' €'
            }
        }
    },
    plugins: {
        legend: {
            onClick: (_e: any, legendItem: any) => {
                const label = legendItem.text;
                if (label.startsWith('+ ')) {
                    const catName = label.slice(2);
                    emit('toggle-category-visibility', catName);
                }
            }
        },
        tooltip: {
            callbacks: {
                label: (ctx: any) => {
                    let label = ctx.dataset.label || '';
                    if (label) label += ': ';
                    if (ctx.parsed.y !== null) {
                        label += new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(ctx.parsed.y);
                    }
                    return label;
                }
            }
        }
    }
}));
</script>

<template>
    <Card class="border-border shadow-sm overflow-hidden mb-20">
        <CardHeader class="flex flex-row items-center justify-between border-b border-border px-10 py-8 relative">
            <div class="flex items-center gap-2">
                <CardTitle class="text-xl font-bold">Évolution de votre Patrimoine</CardTitle>
                <Badge v-if="selectedCategory" variant="finance" class="bg-indigo-50 text-indigo-600 border-indigo-100">
                    Vue Filtrée
                </Badge>
            </div>
            <div v-if="loadingEvolution" class="flex items-center gap-2 text-xs text-muted-foreground italic">
                <RefreshCw class="w-3 h-3 animate-spin" /> Calcul en cours...
            </div>
            <!-- Background indicator for background refresh -->
            <div v-if="isRefreshingChart" class="absolute top-2 right-2 flex items-center gap-2 bg-card/80 backdrop-blur-sm px-2 py-1 rounded-full border border-border shadow-sm">
                <div class="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-ping"></div>
                <span class="text-[9px] font-bold text-muted-foreground uppercase tracking-widest">Rafraîchissement...</span>
            </div>
        </CardHeader>
        <CardContent class="p-12">
            <div v-if="evolutionError" class="bg-red-50 text-red-600 p-8 text-center rounded-xl border border-red-100">
                {{ evolutionError }}
            </div>
            <div v-else class="h-[400px]">
                <Line v-if="chartData.labels.length" :data="chartData" :options="chartOptions" />
                <div v-else class="flex flex-col items-center justify-center h-full text-zinc-400 space-y-2">
                    <Info class="w-8 h-8 opacity-20" />
                    <span class="italic font-medium">Pas assez de données pour tracer le graphique.</span>
                </div>
            </div>
        </CardContent>
    </Card>
</template>
